import React, { useEffect, useState } from "react";
import YhInput from "../../../../../../ui/YhInput";
import YhSelect from "../../../../../../ui/YhSelect";
import YhButton from "../../../../../../ui/YhButton";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../../../../../helpers/methods";
import { getLocationMeta } from "../ProjectLocationList/projectLocationService";
import { addSector, getSectorById } from "./SectorService";
import { getZone } from "../ZoneList/ZoneService";
import notifiers from "../../../../../../global/constants/NotificationConstants";

type AddSectorProps = {
  closeDrawer: () => void;
  sectorId?: string | null;
  isEdit?: boolean;
};

const emptySector = {
  countryId: "",
  stateId: "",
  cityId: "",
  zoneId: "",
  sector_name: "",
  description: "",
};

const AddSector = ({ closeDrawer, sectorId, isEdit }: AddSectorProps) => {
  const [location, setLocation] = useState<any>({
    country: [],
    state: [],
    city: [],
    zone: [],
  });
  const [sector, setSector] = useState<any>({ ...emptySector });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getLocation();
  }, []);

  useEffect(() => {
    if (isEdit && sectorId) {
      fetchSectorById();
    } else {
      setSector({ ...emptySector });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectorId, isEdit]);

  const fetchSectorById = async () => {
    try {
      const res = await getSectorById(sectorId);
      if (res?.success === 1) {
        setSector({
          ...emptySector,
          ...res.data,
          countryId: res.data?.countryId || "",
          stateId: res.data?.stateId || "",
          cityId: res.data?.cityId || "",
          zoneId: res.data?.zoneId || "",
          sector_name: res.data?.sector_name || "",
          description: res.data?.description || "",
        });
      }
    } catch (err) {
      console.error("Error fetching sector:", err);
    }
  };

  const getLocation = async () => {
    try {
      const locationRes = await getLocationMeta();
      const data = locationRes.data || [];

      const countryOptions: any[] = [];
      const stateOptions: any[] = [];
      const cityOptions: any[] = [];
      const zoneOptions: any[] = [];

      data.forEach((country: any) => {
        countryOptions.push({
          value: country._id,
          label: country.country_name,
        });

        country.states?.forEach((state: any) => {
          stateOptions.push({
            value: state._id,
            label: state.name,
            countryId: country._id,
          });

          state.cities?.forEach((city: any) => {
            cityOptions.push({
              value: city._id,
              label: city.name,
              stateId: state._id,
              countryId: country._id,
            });

            city.zones?.forEach((zone: any) => {
              zoneOptions.push({
                value: zone._id,
                label: zone.name,
                cityId: city._id,
                stateId: state._id,
                countryId: country._id,
              });
            });
          });
        });
      });

      setLocation({
        country: countryOptions,
        state: stateOptions,
        city: cityOptions,
        zone: zoneOptions,
      });
    } catch (error: any) {
      openErrorNotification(error.message || notifiers.GENERIC_ERROR);
    }
  };

  const filteredStates =
    location?.state?.filter((s: any) => s.countryId === sector.countryId) || [];
  const filteredCities =
    location?.city?.filter((c: any) => c.stateId === sector.stateId) || [];
  const filteredZones =
    location?.zone?.filter((z: any) => z.cityId === sector.cityId) || [];

  const handleSubmit = async () => {
    try {
      if (!sector.countryId) return openErrorNotification("Country is required");
      if (!sector.stateId) return openErrorNotification("State is required");
      if (!sector.cityId) return openErrorNotification("City is required");
      if (!sector.zoneId) return openErrorNotification("Zone is required");
      if (!sector.sector_name)
        return openErrorNotification("Sector name is required");

      setSubmitting(true);
      const res = await addSector(sector);
      if (res.success === 1) {
        openSuccessNotification(
          isEdit ? "Sector updated successfully" : "Sector added successfully"
        );
        closeDrawer();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40 p-4"
      onClick={closeDrawer}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-[#EEF0F5] px-5 py-4">
          <h2 className="text-lg font-bold text-[#111827]">
            {isEdit ? "Edit Sector" : "Create Sector"}
          </h2>
          <button
            type="button"
            onClick={closeDrawer}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F3F4F6]"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <YhSelect
              label="Country"
              requiredMark
              placeholder="Select Country"
              value={sector.countryId}
              options={location.country || []}
              onChange={(e) =>
                setSector({
                  ...sector,
                  countryId: e.target.value,
                  stateId: "",
                  cityId: "",
                  zoneId: "",
                })
              }
            />

            <YhSelect
              label="State"
              requiredMark
              placeholder="Select State"
              value={sector.stateId}
              options={filteredStates}
              disabled={!sector.countryId}
              onChange={(e) =>
                setSector({
                  ...sector,
                  stateId: e.target.value,
                  cityId: "",
                  zoneId: "",
                })
              }
            />

            <YhSelect
              label="City"
              requiredMark
              placeholder="Select City"
              value={sector.cityId}
              options={filteredCities}
              disabled={!sector.stateId}
              onChange={(e) =>
                setSector({
                  ...sector,
                  cityId: e.target.value,
                  zoneId: "",
                })
              }
            />

            <YhSelect
              label="Zone"
              requiredMark
              placeholder="Select Zone"
              value={sector.zoneId}
              options={filteredZones}
              disabled={!sector.cityId}
              onChange={(e) =>
                setSector({
                  ...sector,
                  zoneId: e.target.value,
                })
              }
            />

            <div className="sm:col-span-2">
              <YhInput
                label="Sector Name"
                requiredMark
                placeholder="Enter Sector Name"
                value={sector.sector_name}
                onChange={(e) =>
                  setSector({
                    ...sector,
                    sector_name: e.target.value,
                  })
                }
              />
            </div>

            <div className="sm:col-span-2">
              <label className="yh-label">Description</label>
              <textarea
                className="yh-input min-h-[120px] resize-y"
                placeholder="Enter description"
                value={sector.description || ""}
                onChange={(e) =>
                  setSector({
                    ...sector,
                    description: e.target.value,
                  })
                }
              />
            </div>
          </div>
        </div>

        <div className="flex shrink-0 justify-end gap-3 border-t border-[#EEF0F5] bg-[#FAFBFC] px-5 py-4">
          <YhButton variant="secondary" size="sm" onClick={closeDrawer}>
            Cancel
          </YhButton>
          <YhButton
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Saving…" : "Submit"}
          </YhButton>
        </div>
      </div>
    </div>
  );
};

export default AddSector;
